import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import './MobileNewsragLayout.css';

const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://knotcord.com';
  }
  const hostname = window.location.hostname;
  // Localhost or LAN IP: always use relative URL so setupProxy.js handles it
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL  = getApiBaseUrl();
const SEARCH_URL    = `${API_BASE_URL}/zoulang/newsrag/search/`;
const LIVE_FEED_URL = `${API_BASE_URL}/zoulang/newsrag/live-feed/`;

const formatPubDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  return isNaN(d) ? raw : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ── Sentiment helpers ── */
const SENTIMENT_META = {
  'bullish':           { bg: '#D4EDD9', fg: '#1E6B34', dot: '#2E8B45' },
  'somewhat bullish':  { bg: '#E8F3E6', fg: '#3A7A52', dot: '#4A9060' },
  'neutral':           { bg: '#EEECEA', fg: '#6A6A64', dot: '#8A8A84' },
  'somewhat bearish':  { bg: '#F5EDE4', fg: '#9A5828', dot: '#B06830' },
  'bearish':           { bg: '#F5E0DC', fg: '#A0261E', dot: '#C02E24' },
};

const sentimentMeta = (label) => {
  if (!label) return null;
  return SENTIMENT_META[label.toLowerCase()] || null;
};

/* ── Sentiment badge ── */
const SentimentBadge = ({ label }) => {
  const m = sentimentMeta(label);
  if (!m) return null;
  return (
    <span className="mnr-badge" style={{ background: m.bg, color: m.fg }}>
      <span className="mnr-badge-dot" style={{ background: m.dot }} />
      {label}
    </span>
  );
};

/* ── Spinner ── */
const Spinner = () => <div className="mnr-spinner" />;

/* ── Detail view: full article list for a ticker ── */
const DetailView = ({ ticker, headlines, onBack }) => (
  <div className="mnr-detail">
    <div className="mnr-detail-header">
      <button className="mnr-back-btn" onClick={onBack}>←</button>
      <span className="mnr-detail-ticker">{ticker}</span>
      <span className="mnr-detail-tag">Sources</span>
    </div>

    <div className="mnr-detail-body">
      {headlines.length === 0 ? (
        <p className="mnr-empty">No sources available</p>
      ) : (
        headlines.map((h, i) => (
          <div key={i} className="mnr-article" style={{ animationDelay: `${i * 0.04}s` }}>
            <div className="mnr-article-meta">
              <span className="mnr-date">{h.date}</span>
              {h.source && <span className="mnr-source">{h.source}</span>}
            </div>
            <a
              href={h.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mnr-article-title"
            >
              {h.title}
            </a>
            {h.summary && <p className="mnr-article-summary">{h.summary}</p>}
          </div>
        ))
      )}
    </div>
  </div>
);

/* ── Live Feed view ── */
const LiveFeedView = ({ ticker, articles, isLoading, error, onBack }) => (
  <div className="mnr-detail">
    <div className="mnr-detail-header">
      <button className="mnr-back-btn" onClick={onBack}>←</button>
      <span className="mnr-detail-ticker">{ticker}</span>
      <span className="mnr-live-tag">
        <span className="mnr-live-dot" />
        Live Feed
      </span>
    </div>

    <div className="mnr-detail-body">
      {isLoading && (
        <div className="mnr-loading" style={{ marginTop: 48 }}>
          <Spinner />
          <p>Loading live feed…</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="mnr-error">{error}</div>
      )}

      {!isLoading && !error && articles.length === 0 && (
        <p className="mnr-empty">No live articles found</p>
      )}

      {!isLoading && !error && articles.length > 0 &&
        articles.map((h, i) => (
          <div key={i} className="mnr-article" style={{ animationDelay: `${i * 0.04}s` }}>
            <div className="mnr-article-meta">
              <span className="mnr-date">{formatPubDate(h.date)}</span>
              {h.source && <span className="mnr-source">{h.source}</span>}
            </div>
            <a
              href={h.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mnr-article-title"
            >
              {h.title}
            </a>
            {h.summary && <p className="mnr-article-summary">{h.summary}</p>}
          </div>
        ))
      }
    </div>
  </div>
);

/* ── Company info helpers ── */
function splitIntoParagraphs(text) {
  if (!text) return [];
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += 3) {
    paragraphs.push(sentences.slice(i, i + 3).join('').trim());
  }
  return paragraphs;
}

const MobileCompanyInfoPanel = ({ ticker, info }) => {
  if (info === 'loading') return <div className="mnr-about-panel"><p className="mnr-about-loading">Loading...</p></div>;
  if (info === 'error' || !info?.description) return <div className="mnr-about-panel"><p className="mnr-about-loading">No description available.</p></div>;
  const paragraphs = splitIntoParagraphs(info.description);
  return (
    <div className="mnr-about-panel">
      <div className="mnr-about-header">
        <span className="mnr-about-name">{info.name || ticker}</span>
        {(info.sector || info.industry) && (
          <span className="mnr-about-meta">
            {[info.sector, info.industry].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
};

/* ── Ticker card ── */
const TickerCard = ({ result, rank, onViewSources, onLiveFeed, aboutInfo, aboutOpen, onToggleAbout }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mnr-card">
      {/* Header — always visible, tap to expand */}
      <div className="mnr-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="mnr-card-top">
          <span className="mnr-rank">{String(rank).padStart(2, '0')}</span>
          <span className="mnr-ticker">{result.ticker}</span>
          {result.sentiment_label && <SentimentBadge label={result.sentiment_label} />}
          <span className="mnr-chevron">{expanded ? '↑' : '↓'}</span>
        </div>

        {result.business_labels?.length > 0 && (
          <div className="mnr-labels">
            {[...new Set(result.business_labels)].map((lbl, i) => (
              <span key={i} className="mnr-label">{lbl}</span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="mnr-card-body">
          {result.summary && (
            <p className="mnr-summary">{result.summary}</p>
          )}

          {result.headlines?.length > 0 && (
            <div className="mnr-headline-previews">
              {result.headlines.slice(0, 2).map((h, i) => (
                <div key={i} className="mnr-headline-preview">
                  <span className="mnr-date">{h.date}</span>
                  <a
                    href={h.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mnr-headline-link"
                    onClick={e => e.stopPropagation()}
                  >
                    {h.title}
                  </a>
                </div>
              ))}
            </div>
          )}

          <div className="mnr-action-row">
            <button
              className="mnr-sources-btn"
              onClick={e => { e.stopPropagation(); onViewSources(result); }}
            >
              View all sources · {result.headlines?.length ?? 0}
            </button>
            <button
              className="mnr-live-btn"
              onClick={e => { e.stopPropagation(); onLiveFeed(result.ticker); }}
            >
              Live Feed
            </button>
            <button
              className={`mnr-about-btn${aboutOpen ? ' mnr-about-btn--active' : ''}`}
              onClick={e => { e.stopPropagation(); onToggleAbout(result.ticker); }}
            >
              About
            </button>
          </div>

          {aboutOpen && (
            <MobileCompanyInfoPanel ticker={result.ticker} info={aboutInfo} />
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main component ── */
const MobileNewsragLayout = () => {
  const { isAuthenticated } = useAuth();

  const [query,       setQuery]       = useState('');
  const [results,     setResults]     = useState(null);
  const [elapsed,     setElapsed]     = useState(null);
  const [mode,        setMode]        = useState(null); // 'ticker' | 'vocab'
  const [matchedTerm, setMatchedTerm] = useState(null);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState(null);
  const [detail,          setDetail]          = useState(null); // { ticker, headlines }
  const [liveFeed,        setLiveFeed]        = useState(null); // { ticker }
  const [liveCache,       setLiveCache]       = useState({});
  const [liveFeedLoading, setLiveFeedLoading] = useState(false);
  const [liveFeedError,   setLiveFeedError]   = useState(null);
  const [aboutCache,      setAboutCache]      = useState({});
  const [aboutExpanded,   setAboutExpanded]   = useState({});
  const inputRef = useRef(null);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;

    setIsLoading(true);
    setError(null);
    setResults(null);
    setDetail(null);

    try {
      const res = await fetch(SEARCH_URL, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ query: q }),
      });

      if (res.status === 401) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Error ${res.status}`);
        return;
      }

      const data = await res.json();
      setResults(data.results);
      setElapsed(data.elapsed);
      setMode(data.mode || 'vocab');
      setMatchedTerm(data.matched_term || null);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setElapsed(null);
    setMode(null);
    setMatchedTerm(null);
    setError(null);
    setDetail(null);
    setLiveFeed(null);
    setLiveCache({});
    setLiveFeedError(null);
    setAboutCache({});
    setAboutExpanded({});
    inputRef.current?.focus();
  };

  const toggleAbout = useCallback((ticker) => {
    setAboutExpanded(prev => {
      const next = { ...prev, [ticker]: !prev[ticker] };
      if (next[ticker] && !aboutCache[ticker]) {
        setAboutCache(prev2 => ({ ...prev2, [ticker]: 'loading' }));
        fetch(`${API_BASE_URL}/zoulang/newsrag/company-info/?symbol=${ticker}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : Promise.reject())
          .then(d => setAboutCache(prev2 => ({ ...prev2, [ticker]: d })))
          .catch(() => setAboutCache(prev2 => ({ ...prev2, [ticker]: 'error' })));
      }
      return next;
    });
  }, [aboutCache]);

  const handleOpenLiveFeed = async (ticker) => {
    setLiveFeedError(null);
    setLiveFeed({ ticker });
    if (liveCache[ticker] !== undefined) return;
    setLiveFeedLoading(true);
    try {
      const res = await fetch(`${LIVE_FEED_URL}?symbols=${ticker}`, { credentials: 'include' });
      if (res.status === 401) { setLiveFeedError('Session expired. Please log in again.'); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLiveCache(prev => ({ ...prev, [ticker]: data.feed[ticker] || [] }));
    } catch {
      setLiveFeedError('Could not load live feed.');
    } finally {
      setLiveFeedLoading(false);
    }
  };

  /* ── Screen: Detail ── */
  if (detail) {
    return (
      <DetailView
        ticker={detail.ticker}
        headlines={detail.headlines}
        onBack={() => setDetail(null)}
      />
    );
  }

  /* ── Screen: Live Feed ── */
  if (liveFeed) {
    return (
      <LiveFeedView
        ticker={liveFeed.ticker}
        articles={liveCache[liveFeed.ticker] || []}
        isLoading={liveFeedLoading}
        error={liveFeedError}
        onBack={() => setLiveFeed(null)}
      />
    );
  }

  /* ── Screen: Main ── */
  return (
    <div className="mnr-container">

      {/* Header */}
      <div className="mnr-header">
        <button className="mnr-back-btn" onClick={() => window.history.back()}>←</button>
        <span className="mnr-title">News Reader</span>
        <span className="mnr-header-tag">RAG</span>
      </div>

      {/* Search bar */}
      <div className="mnr-search">
        <input
          ref={inputRef}
          type="text"
          className="mnr-input"
          placeholder="e.g. data center or NVDA"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          disabled={isLoading}
        />
        <button
          className={`mnr-go-btn${isLoading || !query.trim() ? ' mnr-go-btn--disabled' : ''}`}
          onClick={handleSearch}
          disabled={isLoading || !query.trim()}
        >
          {isLoading ? <Spinner /> : 'Go'}
        </button>
      </div>

      {/* Tips */}
      {!results && !isLoading && !error && (
        <div className="mnr-tips">
          <p className="mnr-tips-title">Tips</p>
          <ul className="mnr-tips-list">
            <li>Enter a ticker (e.g. NVDA) to find similar stocks</li>
            <li>Use keywords to surface ticker symbols</li>
            <li>Results ranked by semantic similarity</li>
          </ul>
        </div>
      )}

      {/* Clear button */}
      {(results || error) && (
        <div className="mnr-clear-row">
          <button className="mnr-clear-btn" onClick={handleClear}>
            Clear results
          </button>
        </div>
      )}

      {/* Body */}
      <div className="mnr-body">

        {!results && !isLoading && !error && (
          <div className="mnr-empty-state">
            <div className="mnr-empty-icon">◎</div>
            <p className="mnr-empty">
              Enter keywords to find<br />stocks by news sentiment
            </p>
          </div>
        )}

        {isLoading && (
          <div className="mnr-loading">
            <Spinner />
            <p>Searching…</p>
          </div>
        )}

        {error && (
          <div className="mnr-error">{error}</div>
        )}

        {results && results.length === 0 && (
          <p className="mnr-empty">No results for "{query}"</p>
        )}

        {results && results.length > 0 && (
          <>
            <div className="mnr-results-meta">
              <span className="mnr-results-count">
                {mode === 'ticker'
                  ? `${results[0]?.ticker} + ${results.length - 1} similar`
                  : matchedTerm && matchedTerm.toLowerCase() !== query.toLowerCase()
                    ? `${results.length} tickers · "${query}" → "${matchedTerm}"`
                    : `${results.length} tickers · "${query}"`
                }
              </span>
              {elapsed && (
                <span className="mnr-elapsed">{elapsed}s</span>
              )}
            </div>

            {results.map((r, i) => (
              <TickerCard
                key={r.ticker}
                result={r}
                rank={i + 1}
                onViewSources={res => setDetail({ ticker: res.ticker, headlines: res.headlines })}
                onLiveFeed={handleOpenLiveFeed}
                aboutInfo={aboutCache[r.ticker]}
                aboutOpen={!!aboutExpanded[r.ticker]}
                onToggleAbout={toggleAbout}
              />
            ))}
          </>
        )}

      </div>
    </div>
  );
};

export default MobileNewsragLayout;
